import { getSessionToken } from "@/modules/auth/hooks/useSession";
import { BaseAPIClientV2, type BaseAPIClientOptions } from "../clientv2";
import {
  EInviteStatus,
  EInviteType,
  EUserStatus,
  type ConsumeKeyCodeRequest,
  type GenerateKeyCodeRequest,
  type IInvestment,
  type IInvite,
  type IKeyCode,
  type ISavedDocument,
  type IUser,
  type ResolveInviteRequest,
  type FindexInvestment,
  type UserDetails,
  type UpdateNotificationStatusesRequest,
  type INotification,
  ENotificationStatus,
  type IProviderImport,
  type ProviderImportRequest,
  type IntegrationProvider,
  type TDocRef,
  type IAttachment,
  getRefId,
  type AssetDiversification,
  type IForexQuote,
  EAttachmentSystemType,
  type IUserWorth,
  type IAssetAdmin,
  type AttachmentUserPermission,
  type ICompany,
  EFileManagerListMode,
  type IReferral,
  type ICryptoQuote,
  type ITicker,
  type IOpportunityInterest,
  type ICommodityQuote,
  type ICompanyProfile,
  type ILiability,
  type IValue,
  type WorldLocation,
  EAssetType,
  type TrendsGraph,
  type IFullNewsRecommendation,
  type IAsset,
  type ILogoInformation,
  getLogoURL,
  type IScoredImage,
  type DocumentId,
  type IWebsite,
  EWebsiteContentType,
  ECrawlerCategory,
  type ICrawlerContent,
  type IAttachmentTree,
  type IFullNewsEntryRecommendation,
  type INewsEntry,
  type FullInvestment,
  type IOpportunity,
  type IOpportunityInquire,
  EDiversificationViewMode,
  type FlanksCreateSession,
  type FlanksInvestment,
  type FlanksSession,
  type PotentialInvestment,
  type ITickerChartPoint
} from "findef";
import type { FindexUser } from "@/types/models/user";
import { uniqueBy } from "@/utils/array";
import { mergeShareholdersWithInvites } from "./mergeShareholders";
import type { FindexAsset } from "@/types/models/asset";
import { urlEncode } from "@/utils/url";
import type { PaginatedResult } from "@/types/pagination";
import * as Sentry from "@sentry/browser";
import type { DateInterval } from "@/types/date";
import { isDate } from "@/utils/is";
import { env } from "@/utils/env";
import type { EViewMode } from "~/composables/useView";

export const BASE_URL: string = env.VITE_APP_SERVICE_IR;

export type FindexAPIClientOptions = Partial<BaseAPIClientOptions> & {};

export type FindexAssetAdmin = Omit<ISavedDocument<IAssetAdmin>, "asset"> & {
  asset: FindexAsset;
};

export type FindexAttachmentUserPermission = Omit<
  AttachmentUserPermission,
  "user"
> & {
  user: FindexUser;
};

export type AttachmentWithPermissions = Omit<
  ISavedDocument<IAttachment>,
  "permissions"
> & {
  permissions: FindexAttachmentUserPermission[];
};

export type IPriceHistoryResponse = {
  historical: ITickerChartPoint[];
  max: number;
  min: number;
};

export {
  mergeShareholdersWithInvites,
  type ShareholderWithStatus,
} from './mergeShareholders';

// Narrow response shape for the public `/keycode/<code>/validate` route. The
// backend never returns `data`, `hashInput`, `createdBy`, etc. on this route —
// only the fields the signup form needs.
export type KeyCodeValidateResponse = {
  _id: string;
  value: string;
  type?: IKeyCode['type'];
  consumed?: boolean;
  reusable?: boolean;
  expiryDate?: Date | string;
  email?: string;
};

export class FindexAPIClient {
  client: BaseAPIClientV2;
  options: FindexAPIClientOptions;

  constructor(options: FindexAPIClientOptions = {}) {
    this.options = options;
    this.client = new BaseAPIClientV2({
      ...options,
      baseUrl: BASE_URL,
      getBearer: async () => {
        return getSessionToken();
      },
    });
  }

  async test() {
    const token = getSessionToken();
    return await fetch("http://localhost:3633/user", {
      headers: {
        authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    });
  }

  async healthCheck() {
    const [resp] = await this.client.get<{ message: string }>("/health");
    if (!resp) return false;
    if (!resp.message) return false;
    return resp.message === "OK";
  }

  // DEV-4398: TIC integration disabled — replaced by SCB. Body preserved for future re-enable.
  async ticHealthCheck() {
    return false;
    // try {
    //   const [resp] = await this.client.get<{ ok: boolean }>("/tic/health");
    //   return resp?.ok === true;
    // } catch (e) {
    //   Sentry.captureException(e);
    //   return false;
    // }
  }

  async fmpHealthCheck() {
    try {
      const [resp] = await this.client.get<{ ok: boolean }>("/fmp/health");
      return resp?.ok === true;
    } catch (e) {
      Sentry.captureException(e);
      return false;
    }
  }

  async whoami(token?: string) {
    // Authorization header instead of ?token= query param: the legacy query
    // path bypassed signature verification, and the API's JWT middleware
    // reads only the header. The explicit token is sent via
    // args.request.headers, which BaseAPIClientV2 merges last and so
    // overrides the bearer the client would otherwise pull from session
    // state. (DEV-4464)
    const args = token
      ? { request: { headers: { Authorization: `Bearer ${token}` } } }
      : {};
    return await this.client.get<ISavedDocument<IUser, string>>(`/user/me`, args);
  }

  async createInvite(invite: Partial<IInvite>) {
    return await this.client.post<ISavedDocument<IInvite>>(`/invite/send`, {
      payload: invite,
    });
  }

  async resendInvite(params: { inviteId?: string; email?: string; assetId?: string }) {
    return await this.client.post<{ message: string }>(`/invite/resend`, {
      payload: params,
    });
  }

  urlForAttachment(
    attachment: TDocRef<IAttachment> | ISavedDocument<IAttachment> | string,
    download: boolean = false,
  ) {
    if (!attachment) return "";
    if (!download)
      return `${BASE_URL}/attachment/${getRefId(attachment)}/viewPublic`;
    const token = getSessionToken();
    return `${BASE_URL}/attachment/${getRefId(attachment)}/download?token=${token}`;
  }

  urlForLogoQuery(query: string): string {
    return `${BASE_URL}/logo/${query}`;
  }

  urlForLogo(logo: ILogoInformation | TDocRef<ILogoInformation> | string | null | undefined): string | undefined  {
    if (logo === null || typeof logo === 'undefined') return undefined;
    if (typeof logo === 'object') {
      const img = logo as ILogoInformation;
      if (img.image) {
        return this.urlForAttachment(img.image);
      }
    }
    return getLogoURL(logo, env.VITE_APP_LOGO_PUBLIC_KEY);
  }

  urlForTickerLogo(ticker: string) {
    if (ticker.includes('.')) {
      const parts = ticker.split('.');
      if (parts[1] === 'L') {
        ticker = parts[0];
      }
    }

    const encoded = urlEncode({
      token: env.VITE_APP_LOGO_PUBLIC_KEY,
      retina: 'true'
    })
    return `https://img.logo.dev/ticker/${ticker}?${encoded}`
  }

  async updateAssetShareholder(
    assetId: string,
    shareholderEmail: string,
    data: {
      firstname?: string;
      lastname?: string;
      email?: string;
      investments?: Partial<IInvestment>[];
      shareholderType?: string;
      ownedBy?: {
        name?: string;
        organizationNbr?: string;
      };
    },
  ) {
    return await this.client.patch<any>(
      `/asset/${assetId}/shareholder/${shareholderEmail}/update`,
      { payload: data },
    );
  }
  async getAssetInvites(assetId: string) {
    return await this.client.get<ISavedDocument<IInvite, string>[]>(
      `/asset/${assetId}/invites`,
    );
  }

  async getAssetShareholders(assetId: string) {
    const [items, resp] = await this.client.get<FindexUser[]>(
      `/asset/${assetId}/shareholders`,
    );
    const [invites] = await this.getAssetInvites(assetId);
    return [mergeShareholdersWithInvites(items as FindexUser[] | undefined, invites), resp];
  }

  async removeShareholderFromAsset(
    shareholderIdOrEmail: string,
    assetId: string,
  ) {
    return await this.client.delete(
      `/asset/${assetId}/shareholder/${shareholderIdOrEmail}`,
    );
  }

  async generateKeyCode(data: GenerateKeyCodeRequest) {
    return await this.client.post<ISavedDocument<IKeyCode, string>[]>(
      `/keycode`,
      { payload: data },
    );
  }

  async getKeyCodes() {
    return await this.client.get<ISavedDocument<IKeyCode, string>[]>(
      `/keycode/list`,
    );
  }

  async deleteKeyCode(id: string) {
    return await this.client.delete(`/keycode/${id}`);
  }

  async validateKeyCode(code: string): Promise<boolean> {
    const [keycode, resp] = await this.client.get<KeyCodeValidateResponse>(
      `/keycode/${code}/validate`,
    );
    if (!keycode || resp.response?.status !== 200) return false;
    // Backend already rejects (HTTP 400) on consumed-non-reusable; reusable
    // codes come back with `consumed: true` and are still valid for signup.
    return !keycode.consumed || !!keycode.reusable;
  }

  // Looks up the associated email for a waitlist-issued code so the signup
  // form can pre-fill it (keeps the email out of the URL — see
  // backend/src/db/models/waitlist/facade.ts for why).
  async lookupKeyCode(
    code: string,
  ): Promise<KeyCodeValidateResponse | null> {
    const [keycode, resp] = await this.client.get<KeyCodeValidateResponse>(
      `/keycode/${code}/validate`,
    );
    if (!keycode || resp.response?.status !== 200) return null;
    return keycode;
  }

  async consumeKeyCode(code: string, data?: ConsumeKeyCodeRequest) {
    return await this.client.post<ISavedDocument<IKeyCode, string>>(
      `/keycode/${code}/consume`,
      {
        payload: data || {},
      },
    );
  }

  async resolveInvite(data: ResolveInviteRequest) {
    return await this.client.post(`/invite/resolve`, { payload: data });
  }

  async getAssetById(assetId: string) {
    const asset = await this.client.get<FindexAsset>(`/asset/${assetId}`);
    return asset
  }

  async getAssetAdminsByAsset(assetId: string) {
    return await this.client.get<ISavedDocument<IAssetAdmin>[]>(`/assetAdmin/asset/${assetId}`);
  }

  async getAssetTooltipSummary(assetId: string) {
    return await this.client.get<{
      shareholders: FindexUser[]
      invites: ISavedDocument<IInvite, string>[]
      admins: ISavedDocument<IAssetAdmin>[]
    }>(`/asset/${assetId}/tooltipSummary`);
  }

  async getAssetAdmins(assetId: string) {
    return await this.client.get<ISavedDocument<IAssetAdmin, string>[]>(`/asset/${assetId}/admins`);
  }

  async getAssetAdminsByUser(userId: string) {
    console.log("getAssetAdminsByUser", userId)
    return await this.client.get<FindexAssetAdmin[]>(
      `/assetAdmin/user/${userId}`,
    );
  }

  async getAdministratedAssets() {
    return await this.client.get<FindexAsset[]>(`/user/administratedAsset`);
  }

  async getInvestmentById(investmentId: string) {
    return await this.client.get<FindexInvestment>(
      `/user/investments/${investmentId}`,
    );
  }

  async getInvestmentParent(investmentId: string) {
    return await this.client.get<FindexInvestment | null>(
      `/investment/${investmentId}/parent`,
    );
  }

  async getInvestmentsCount(
    query: {
      assetType?: EAssetType;
      listed?: boolean;
    } = {},
  ) {
    const params = urlEncode(query);
    return await this.client.get<{ count: number }>(
      `/user/investments/count?${params}`,
    );
  }

  async getUserDetails() {
    return await this.client.get<UserDetails>(`/user/details`);
  }

  async getUserWorth() {
    return await this.client.get<ISavedDocument<IUserWorth>>(`/user/worth`);
  }

  async updateDiversificationViewMode(viewMode: EDiversificationViewMode) {
    return await this.client.patch<ISavedDocument<IUser>>(
      `/user/diversificationViewMode`,
      { payload: { diversificationViewMode: viewMode } },
    );
  }

  async updateNewsViewMode(viewMode: EViewMode) {
    return await this.client.patch<ISavedDocument<IUser>>(
      `/user/newsViewMode`,
      {payload:{newsViewMode:viewMode}}
    )
  }

  async getInvites() {
    return this.client.get<ISavedDocument<IInvite>[]>("/invite")
  }

  async sendEmailVerification() {
    return await this.client.post<{ status: string }>(
      `/user/sendEmailVerification`,
      {},
    );
  }

  async sendEmailVerificationCode() {
    return await this.client.post<any>(`/user/verification/send`, {});
  }

  async verifyEmailCode(code: string) {
    return await this.client.post<{ message: string }>(
      `/user/verification/${code}`,
      {},
    );
  }

  async sendEmailPasswordReset(email: string, brand?: string) {
    return await this.client.post<{ message: string }>(`/user/sendEmailPasswordReset`, {
      payload: {
        email,
        ...(brand ? { brand } : {}),
      },
    });
  }

  async resetPassword(email: string, token: string, password: string) {
    return await this.client.post<{ message: string }>(`/user/resetPassword`, {
      payload: { email, token, password },
    });
  }

  async changePassword(oldPassword: string, password: string, signOutOtherDevices: boolean) {
    return await this.client.post<{ message: string }>(`/user/changePassword`, {
      payload: {
        oldPassword,
        password,
        signOutOtherDevices,
      },
    });
  }

  async checkResetPasswordToken(email: string, token: string) {
    const params = new URLSearchParams();
    params.set("email", email);
    params.set("token", token);
    return await this.client.get<{ valid: boolean; message?: string; deleted?: boolean }>(
      `/user/resetPassword/check?${params.toString()}`,
    );
  }

  async getNotifications(
    status: ENotificationStatus = ENotificationStatus.UNREAD,
  ) {
    const params = new URLSearchParams({
      status: status,
    });
    return await this.client.get<ISavedDocument<INotification, string>[]>(
      `/user/notifications?${params.toString()}`,
    );
  }

  async updateNotificationStatuses(data: UpdateNotificationStatusesRequest) {
    return await this.client.post(`/user/notifications/updateStatuses`, {
      payload: data,
    });
  }

  async setNotificationStatus(notificationId: string, status: ENotificationStatus) {
    return await this.client.post(`/notification/${notificationId}/setStatus`, { payload: { status } });
  }

  async getFinsquidToken() {
    return await this.client.get<{ token: string }>("/finsquid/token");
  }

  async getFinsquidProviders() {
    return await this.client.get<IntegrationProvider[]>("/finsquid/provider");
  }

  async getFinsquidStatus() {
    return await this.client.get<{ available: boolean }>("/finsquid/status");
  }

  async getProviderImport(providerId: number, sid?: string) {
    try {
      return await this.client.get<IProviderImport>(
        `/finsquid/import/${providerId}`,
        {
          request: { headers: { sid: sid || "" } },
        },
      );
    } catch (e) {
      Sentry.captureException(e);
      throw e;
    }
  }

  async getProviderImports() {
    try {
      return await this.client.get<IProviderImport[]>(`/finsquid/import`);
    } catch (e) {
      Sentry.captureException(e);
      throw e;
    }
  }

  async getAdminProviderImports(options?: {
    queryString?: string;
    offset?: number;
    limit?: number;
    sort?: Record<string, number>;
  }) {
    const params = options
      ? urlEncode({
          ...options,
          ...(options.sort
            ? {
                sort: JSON.stringify(options.sort),
              }
            : {}),
        })
      : "";
    return await this.client.get<ISavedDocument<IProviderImport, string>[]>(
      `/finsquid/admin?${params}`,
    );
  }

  async upsertProviderImport(data: ProviderImportRequest) {
    try {
      return await this.client.post(`/finsquid/import`, {
        payload: { ...data, doNotRun: true },
      });
    } catch (e) {
      Sentry.captureException(e);
      throw e;
    }
  }

  async runProviderImport(data: ProviderImportRequest) {
    try {
      return await this.client.post<IProviderImport>(`/finsquid/import`, {
        payload: data,
      });
    } catch (e) {
      Sentry.captureException(e);
      throw e;
    }
  }

  async updateProviderImportStatus(providerId: number, sid?: string) {
    try {
      return await this.client.get<IProviderImport>(
        `/finsquid/import/${providerId}/updateStatus`,
        {
          request: { headers: { sid: sid || "" } },
        },
      );
    } catch (e) {
      Sentry.captureException(e);
    }
  }

  async getIntegrationProviders() {
    try {
      return await this.client.get<IntegrationProvider[]>(`/finsquid/provider`);
    } catch (e) {
      Sentry.captureException(e);
      throw e;
    }
  }

  async clearUserData() {
    return await this.client.post(`/developer/user/clear`);
  }

  async getFXPrice(currencyFrom: string, currencyTo: string) {
    const params = new URLSearchParams({
      currencyFrom,
      currencyTo,
    });
    return await this.client.get<{ price: number }>(
      `/finance/fxPrice?${params.toString()}`,
    );
  }

  async deleteSession() {
    return await this.client.delete(`/user/session`);
  }

  async getDiversification() {
    return await this.client.get<AssetDiversification>(
      `/user/graphs/diversification`,
    );
  }

  async importMockPorfolio(seed: number, min: number = 10, max: number = 256) {
    return await this.client.post<{ count: number }>(`/mock/import`, {
      payload: { seed, min, max },
    });
  }

  async getForexQuotes() {
    return await this.client.get<IForexQuote[]>(`/currency`);
  }

  async getForexQuoteHistory(args: {
    tickerFrom: string;
    tickerTo: string;
    year: number;
    month: number;
    offset?: number;
    limit?: number;
  }) {
    const params = urlEncode(args);
    return await this.client.get<IForexQuote[]>(`/currency/history?${params}`);
  }

  async getForexQuoteHistoryCount(args: {
    tickerFrom: string;
    tickerTo: string;
    year: number;
    month: number;
  }) {
    const params = urlEncode(args);
    return await this.client.get<{ count: number }>(
      `/currency/history/count?${params}`,
    );
  }

  async initDirectoryTree() {
    return await this.client.patch(`/attachment/init`);
  }

  async getUserAttachmentsCount() {
    return await this.client.get<{ count: number }>(`/user/attachment/count`);
  }

  async getAttachments() {
    return await this.client.get<ISavedDocument<IAttachment>[]>(`/attachment`);
  }

  async getAttachmentBySystemType(systemType: EAttachmentSystemType) {
    return await this.client.get<ISavedDocument<IAttachment>>(
      `/attachment/system/${systemType}`,
    );
  }

  async getUserUploadsDirectory() {
    return await this.getAttachmentBySystemType(
      EAttachmentSystemType.USER_UPLOADS_DIRECTORY,
    );
  }

  async getUserRootDirectory() {
    return await this.client.get<ISavedDocument<IAttachment>>(
      `/user/attachment/root`,
    );
  }

  async getUserRootDirectoryTree() {
    return await this.client.get<IAttachmentTree>(
      `/user/attachment/tree`,
    );
  }

  async getDirectoryTree(attachmentId: string) {
    return await this.client.get<IAttachmentTree>(
      `/attachment/${attachmentId}/tree`,
    );
  }

  async getAttachment(id: string) {
    const actualId = id === "/" ? "root" : id;
    return await this.client.get<ISavedDocument<IAttachment>>(
      `/attachment/${actualId}`,
    );
  }

  async getAttachmentsSharedWithMe(options?: { parentId?: string }) {
    const params = options ? urlEncode(options) : "";
    return await this.client.get<ISavedDocument<IAttachment>[]>(
      `/attachment/sharedWithMe?${params}`,
    );
  }

  async getDirectoryChildren(id: string) {
    return await this.client.get<IAttachmentTree[]>(
      `/attachment/${id}/flatChildren`,
    );
  }

  async getAttachmentChildren(
    id: string,
    options?: {
      offset?: number;
      limit?: number;
      queryString?: string;
      sharedWithMe?: boolean;
      listMode?: EFileManagerListMode;
    },
  ) {
    const params = options
      ? urlEncode(options, true, { removeUndefined: true })
      : "";
    const actualId = id === "/" ? "root" : id;
    return await this.client.get<PaginatedResult<ISavedDocument<IAttachment>>>(
      `/attachment/${actualId}/children?${params}`,
    );
  }

  async getAttachmentChildrenCount(
    id: string,
    options?: {
      queryString?: string;
      sharedWithMe?: boolean;
      listMode?: EFileManagerListMode;
    },
  ) {
    const params = options
      ? urlEncode(options, true, { removeUndefined: true })
      : "";
    const actualId = id === "/" ? "root" : id;
    return await this.client.get<{ count: number }>(
      `/attachment/${actualId}/children/count?${params}`,
    );
  }

  async getAttachmentPermissions(id: string) {
    return await this.client.get<FindexAttachmentUserPermission[]>(
      `/attachment/${id}/permissions`,
    );
  }

  async updateAttachmentPermissions(
    id: string,
    permissions: AttachmentUserPermission[],
  ) {
    return await this.client.post<ISavedDocument<IAttachment>>(
      `/attachment/${id}/permissions`,
      {
        payload: { permissions },
      },
    );
  }

  async createAttachmentChild(parentId: string, data: Partial<IAttachment>) {
    return await this.client.put<ISavedDocument<IAttachment>>(
      `/attachment/${parentId}/children`,
      {
        payload: data,
      },
    );
  }

  async renameAttachment(id: string, newName: string) {
    return await this.client.patch(`/attachment/${id}`, { payload: { name: newName } });
  }

  async deleteAttachment(id: string) {
    return await this.client.delete(`/attachment/${id}`);
  }

  async getInvestmentDirectory(investmentId: string) {
    return await this.client.get<ISavedDocument<IAttachment>>(
      `/user/investments/${investmentId}/directory`,
    );
  }

  async getLiabilityDirectoryTree(liabilityId: string) {
    return await this.client.get<IAttachmentTree>(
      `/liability/${liabilityId}/directory/tree`,
    );
  }

  async getAssetDirectoryTree(assetId: string) {
    return await this.client.get<IAttachmentTree>(
      `/asset/${assetId}/directory/tree`,
    );
  }

  async getAssetPrivateUserDirectoryTree(assetId: string) {
    return await this.client.get<IAttachmentTree>(
      `/asset/${assetId}/userDirectory/tree`,
    );
  }

  async getAssetIRDirectory(assetId: string) {
    return await this.client.get<ISavedDocument<IAttachment>>(
      `/asset/${assetId}/directory/ir`,
    );
  }

  async getAssetPublicDirectory(assetId: string) {
    return await this.client.get<ISavedDocument<IAttachment, string>>(
      `/asset/${assetId}/publicDirectory`,
    );
  }

  async getAssetPublicDirectoryTree(assetId: string) {
    return await this.client.get<IAttachmentTree>(
      `/asset/${assetId}/publicDirectory/tree`,
    );
  }

  async getAssetUserDirectory(assetId: string) {
    return await this.client.get<ISavedDocument<IAttachment, string>>(
      `/asset/${assetId}/userDirectory`,
    );
  }

  async getAssetAttachmentCount(assetId: string) {
    return await this.client.get<{ count: number }>(
      `/asset/${assetId}/attachment/count`,
    );
  }

  async getInvestmentAttachmentCount(investmentId: string) {
    return await this.client.get<{ count: number }>(
      `/investment/${investmentId}/attachment/count`,
    );
  }

  async getUserFriends(options?: { queryString: string }) {
    const params = options ? urlEncode(options) : "";
    return await this.client.get<FindexUser[]>(`/user/friends?${params}`);
  }

  async getCompaniesCount(options?: { queryString: string }) {
    const params = options ? urlEncode(options) : "";
    return await this.client.get<{ count: number }>(`/company/count?${params}`);
  }

  async getCompanies(options?: {
    queryString?: string;
    offset?: number;
    limit?: number;
    sort?: Record<string, number>;
  }) {
    const params = options
      ? urlEncode({
          ...options,
          ...(options.sort
            ? {
                sort: JSON.stringify(options.sort),
              }
            : {}),
        })
      : "";
    return await this.client.get<ISavedDocument<ICompany>[]>(
      `/company?${params}`,
    );
  }

  async deleteReferral(id: string) {
    return await this.client.delete(`/referral/${id}`);
  }

  async resendReferral(id: string) {
    return await this.client.post(`/referral/${id}/send`, { payload: {} });
  }

  async createReferral(referral: Omit<IReferral, "createdBy">) {
    return await this.client.post<ISavedDocument<IReferral>>(`/referral`, {
      payload: referral,
    });
  }

  async getReferrals(options: {
    offset?: number;
    limit?: number;
  } = {}) {
    const params = urlEncode(options);
    return await this.client.get<Array<IReferral>>(`/referral?${params}`)
  }

  async getCryptoQuotes(options?: {
    queryString?: string;
    offset?: number;
    limit?: number;
  }) {
    const params = options ? urlEncode(options) : "";
    return await this.client.get<ISavedDocument<ICryptoQuote>[]>(
      `/crypto?${params}`,
    );
  }

  async getCryptoQuoteById(id: string) {
    return await this.client.get<ISavedDocument<ICryptoQuote>>(`/crypto/${id}`);
  }

  async ensureCryptoQuoteImage(id: string) {
    return await this.client.post<ISavedDocument<ICryptoQuote>>(
      `/crypto/${id}/image`,
      { payload: {} },
    );
  }

  async ensureCompanyProfileImage(id: string) {
    return await this.client.post<ISavedDocument<ICompanyProfile>>(
      `/company-profile/${id}/image`,
      { payload: {} },
    );
  }

  async getCommodityQuotes(options?: {
    queryString?: string;
    offset?: number;
    limit?: number;
    sort?: Record<string, number>;
  }) {
    const params = options
      ? urlEncode({
          ...options,
          ...(options.sort
            ? {
                sort: JSON.stringify(options.sort),
              }
            : {}),
        })
      : "";
    return await this.client.get<ISavedDocument<ICommodityQuote, string>[]>(
      `/commodity?${params}`,
    );
  }

  async getTickerSuggestions() {
    return await this.client.get<ISavedDocument<ITicker>[]>(
      `/ticker/suggestions`,
    );
  }

  async getCompanySuggestions() {
    // TODO: fetch company suggestions
    return [];
  }

  async queryTicCompanies(limit: number = 16, queryString?: string) {
    const params = urlEncode({
      limit: (limit || 0).toString(),
      queryString: queryString || "",
    });
    return await this.client.get<ISavedDocument<ICompanyProfile, string>[]>(
      `/swedish-company?${params}`,
    );
  }

  async getCompanyProfiles(options?: {
    queryString?: string;
    listed?: boolean;
    offset?: number;
    limit?: number;
    sort?: Record<string, number>;
  }) {
    const params = options
      ? urlEncode({
          ...options,
          ...(options.sort
            ? {
                sort: JSON.stringify(options.sort),
              }
            : {}),
        })
      : "";
    return await this.client.get<ISavedDocument<ICompanyProfile, string>[]>(
      `/company-profile?${params}`,
    );
  }

  async getCompanyProfilesCount(options?: { queryString?: string }) {
    const params = options
      ? urlEncode({
          ...options,
        })
      : "";
    return await this.client.get<{ count: number }>(
      `/company-profile/count?${params}`,
    );
  }

  async getCompanyProfileById(id: string) {
    return await this.client.get<ISavedDocument<ICompanyProfile, string>>(
      `/company-profile/${id}`,
    );
  }

  async createOpportunityInterest(data: {
    note: string;
    opportunityType: string;
    requestedType?: string;
  }) {
    return await this.client.post<ISavedDocument<IOpportunityInterest>>(
      `/opportunity/interest`,
      { payload: data },
    );
  }

  async createCompanyProfile(data: Partial<ICompanyProfile>) {
    return await this.client.post<ISavedDocument<ICompanyProfile, string>>(
      `/company-profile`,
      { payload: data },
    );
  }

  async updateCompanyProfile(id: string, data: Partial<ICompanyProfile>) {
    return await this.client.patch<ISavedDocument<ICompanyProfile, string>>(
      `/company-profile/${id}`,
      { payload: data },
    );
  }

  async createCommodityQuote(data: Partial<ICommodityQuote>) {
    return await this.client.post<ISavedDocument<ICommodityQuote, string>>(
      `/commodity`,
      { payload: data },
    );
  }

  async createCryptoQuote(data: Partial<ICryptoQuote>) {
    return await this.client.post<ISavedDocument<ICryptoQuote, string>>(
      `/crypto`,
      { payload: data },
    );
  }

  async updateCryptoQuote(id: string, data: Partial<ICryptoQuote>) {
    return await this.client.patch<ISavedDocument<ICryptoQuote, string>>(
      `/crypto/${id}`,
      { payload: data },
    );
  }

  async getUserLastLogin() {
    return await this.client.get<{ lastLogin: string | Date }>(
      `/user/lastLogin`,
    );
  }

  async updateUserLastLogin() {
    return await this.client.post(`/user/lastLogin`, { payload: {} });
  }

  async getUserSession() {
    return await this.client.get<{ expired: boolean }>(`/user/session`);
  }

  async getInvestmentPriceHistory(
    investmentId: string,
    interval?: DateInterval,
  ) {
    const a =
      interval && isDate(interval.start) ? interval.start.getTime() : "";
    const b = interval && isDate(interval.end) ? interval.end.getTime() : "";

    const params = new URLSearchParams({
      start: a.toString(),
      end: b.toString(),
    });
    return await this.client.get<IPriceHistoryResponse>(
      `/investment/${investmentId}/priceHistory?${params.toString()}`,
    );
  }

  async createLiability(data: Partial<ILiability>) {
    return await this.client.post<ISavedDocument<ILiability, string>>(
      `/liability`,
      { payload: data },
    );
  }

  async updateLiability(id: string, data: Partial<ILiability>) {
    return await this.client.patch<ISavedDocument<ILiability, string>>(
      `/liability/${id}`,
      { payload: data },
    );
  }

  async getLiabilities(options?: {
    queryString?: string;
    offset?: number;
    limit?: number;
    sort?: Record<string, number>;
  }) {
    const params = options
      ? urlEncode({
          ...options,
          ...(options.sort
            ? {
                sort: JSON.stringify(options.sort),
              }
            : {}),
        })
      : "";
    return await this.client.get<ISavedDocument<ILiability, string>[]>(
      `/liability?${params}`,
    );
  }

  async getLiabilitiesCount(options?: {
    queryString?: string;
    offset?: number;
    limit?: number;
    sort?: Record<string, number>;
  }) {
    const params = options
      ? urlEncode({
          ...options,
          ...(options.sort
            ? {
                sort: JSON.stringify(options.sort),
              }
            : {}),
        })
      : "";
    return await this.client.get<{ count: number }>(
      `/liability/count?${params}`,
    );
  }

  async getLiabilityById(id: string) {
    return await this.client.get<ISavedDocument<ILiability, string>>(
      `/liability/${id}`,
    );
  }

  async getLiabilityDirectory(liabilityId: string) {
    return await this.client.get<ISavedDocument<IAttachment>>(
      `/liability/${liabilityId}/directory`,
    );
  }

  async deleteLiabilityById(id: string, keepDirectory: boolean = false) {
    const params = keepDirectory ? '?keepDirectory=true' : '';
    return await this.client.delete(`/liability/${id}${params}`);
  }

  async checkLiabilityDirectoryHasDocuments(liabilityIds: string[]) {
    return await this.client.post<Array<{ liabilityId: string; hasDocuments: boolean }>>(`/liability/directory/hasDocuments`, {
      payload: { liabilityIds }
    });
  }

  async deleteLiabilityDirectory(liabilityId: string) {
    return await this.client.delete(`/liability/${liabilityId}/directory`);
  }

  async getTotalRemainingDebt() {
    return await this.client.get<IValue>(`/liability/totalRemainingDebt`);
  }

  async getPortfolioValuation() {
    return await this.client.get<IValue>(`/user/investments/portfolioValuation`);
  }

  async getTotalInvested() {
    return await this.client.get<IValue>(`/user/investments/totalInvested`);
  }

  async getTotalLiquidCash() {
    return await this.client.get<IValue>(`/user/totalLiquidCash`);
  }

  async getSnapshotCount() {
    return await this.client.get<{ count: number }>(`/user/snapshots/count`);
  }

  async getWorldLocations(args: {
    queryString: string;
    offset?: number;
    limit?: number;
  }) {
    const params = urlEncode({
      ...args,
    });
    return await this.client.get<WorldLocation[]>(`/geo/search?${params}`);
  }

  async getWorldLocation(args: { assetId: string }) {
    const [data] = await this.client.get<WorldLocation>(
      `/geo/searchAssetAddress?assetId=${args.assetId}`,
    );
    return data;
  }

  async getClosestWordLocation(args: { lat: number; lng: number }) {
    const params = urlEncode({
      ...args,
    });
    return await this.client.get<WorldLocation>(`/geo/closest?${params}`);
  }

  async getTrendsGraph(interval?: DateInterval, fmt?: string) {
    const a =
      interval && isDate(interval.start) ? interval.start.getTime() : "";
    const b = interval && isDate(interval.end) ? interval.end.getTime() : "";

    const params = new URLSearchParams({
      start: a.toString(),
      end: b.toString(),
    });

    if (fmt) {
      params.set("fmt", fmt);
    }

    return await this.client.get<TrendsGraph>(
      `/user/graphs/tmp?` + params.toString(),
    );
  }

  async getAINewsRecommendations(
    skip: number = 0,
    limit: number = 16,
    filter?: Partial<IAsset>,
    queryString?: string,
  ) {
    const params = urlEncode({
      skip: (skip || 0).toString(),
      limit: (limit || 0).toString(),
      queryString: queryString || "",
      assetType: filter?.type,
      listed: filter?.listed,
    });
    return await this.client.get<IFullNewsRecommendation[]>(
      `/ai-news?${params}`,
    );
  }


  async dislikeNewsArticle(articleId: string) {
    const [resp] = await this.client.post<{ message: string }>(`/news/${articleId}/dislike`, { payload: {} });
    return resp;
  }

  async markAsRead(recommendationId: DocumentId) {
    const [resp] = await this.client.post<{ message: string }>(`/newsfeed/recommendation/${recommendationId}/isRead`, {
      payload:{}
    })
    return resp
  }

  async searchLogos(args: {
    query: string;
    offset?: number;
    limit?: number;
    minScore?: number;
    sorting?: Record<string, number>
  }) {
    return await this.client.post<IScoredImage[]>(`/logo/search`, { payload: args });
  }

  async getLogoCount(args: {
    query?: string;
    minScore?: number;
  } = {}) {
    const [results] = await this.client.post<{ count: number }>(`/logo/count`, { payload: args });
    return results?.count ?? 0;
  }


  async declineInvite(inviteId: DocumentId) {
    return await this.client.post(`/invite/${inviteId}/decline`)
  }

  async deleteInvite(inviteId: DocumentId) {
    return await this.client.post(`/invite/${inviteId}/delete`)
  }

  async deleteInviteByAsset(assetId: DocumentId) {
    return await this.client.delete(`/invite/asset/${assetId}`)
  }

  async searchScraper(args: {
    query: string;
    offset?: number;
    limit?: number;
    sorting?: Record<string, number>;
    contentType?: EWebsiteContentType;
  }) {
    return await this.client.post<IWebsite[]>(`/scraper`, { payload: args });
  }

  async getScraperCount(args: {
    query: string;
    offset?: number;
    limit?: number;
    sorting?: Record<string, number>;
    contentType?: EWebsiteContentType;
  }) {
    const [results] = await this.client.post<{ count: number }>(`/scraper/count`, { payload: args });
    return results?.count ?? 0;
  }

  async findLogos(args: {
    query: string;
    offset?: number;
    limit?: number;
    sorting?: Record<string, number>
  }) {
    return await this.client.post<{logo: ISavedDocument<IAttachment, string>, score: number}[]>(`/attachment/logo`, { payload: args });
  }

  async crawlerSearch(args: {
    query: string;
    offset?: number;
    limit?: number;
    sorting?: Record<string, number>,
    category?: ECrawlerCategory
  }) {
    return await this.client.post<ICrawlerContent[]>(`/crawler`, { payload: args });
  };

  async crawlerSearchCount(args: {
    query?: string;
    offset?: number;
    limit?: number;
    sorting?: Record<string, number>,
    category?: ECrawlerCategory
  }) {
    const [data] = await this.client.post<{ count: number }>(`/crawler/count`, { payload: args });
    return data?.count ?? 0;
  };


  async getUserNewsEntryRecommendations(args: {
    queryString?: string;
    offset?: number;
    limit?: number;
    assetType?: EAssetType;
    assetListed?: boolean;
  } = {}) {
    return await this.client.post<Array<IFullNewsEntryRecommendation>>(`/newsfeed/userRecommendations`, { payload: args });
  }

  async getUserNewsEntryRecommendationsCount(args: {
    queryString?: string;
    assetType?: EAssetType;
    assetListed?: boolean;
  } = {}) {
    const [data] = await this.client.post<{ count: number }>(`/newsfeed/userRecommendations`, { payload: {...args, count: true} });
    return data?.count ?? 0;
  }

  async getAssetNewsEntries(assetId: string, args: {
    queryString?: string;
    offset?: number;
    limit?: number;
  } = {}) {
    return await this.client.post<Array<INewsEntry>>(`/newsfeed/asset/${assetId}`, { payload: args });
  }

  async getAssetNewsEntriesCount(assetId: string, args: {
    queryString?: string;
  } = {}) {
    const [data] = await this.client.post<{ count: number }>(`/newsfeed/asset/${assetId}`, { payload: {...args, count: true} });
    return data?.count ?? 0;
  }

  async getCompanyProfileNewsEntries(companyProfileId: string, args: {
    queryString?: string;
    offset?: number;
    limit?: number;
  }) {
    return await this.client.post<Array<INewsEntry>>(`/newsfeed/companyProfile/${companyProfileId}`, { payload: args });
  }

  async getCompanyProfileNewsEntriesCount(companyProfileId: string, args: {
    queryString?: string;
  }) {
    const [data] = await this.client.post<{ count: number }>(`/newsfeed/companyProfile/${companyProfileId}`, { payload: {...args, count: true} });
    return data?.count ?? 0;
  }

  async dislikeNewsRecommendation(recommendationId: string) {
    return await this.client.post<{ message: string }>(`/newsfeed/recommendation/${recommendationId}/dislike`, { payload: {} });
  }

  async getInvestmentsByAssetId(assetId: string) {
    return await this.client.get<Array<FullInvestment>>(`/investment/byAssetId/${assetId}`);
  }


  async getOpportunities(options: {
    offset?: number;
    limit?: number;
    query?: string;
    opportunityType?: string;
    assetType?: string;
  } = { offset: 0, limit: 0 }) {
    const params = urlEncode(options);
    return await this.client.get<Array<ISavedDocument<IOpportunity, string>>>(`/opportunity?${params}`);
  }

  async getOpportunitiesCount(options: {
    query?: string;
    opportunityType?: string;
    assetType?: string;
  } = {}) {
    const params = urlEncode({...options, count: true, offset: 0, limit: 0});
    const [resp] = await this.client.get<{ count: number }>(`/opportunity?${params}`);
    return resp?.count ?? 0;
  }

  async createOpportunityInquire(opportunityId: string, data: Omit<IOpportunityInquire, 'opportunity'> & { phone?: string }) {
    return await this.client.post<ISavedDocument<IOpportunityInquire, string>>(`/opportunity/${opportunityId}/inquire`, { payload: data });
  }

  async enableUserOpportunities() {
    return await this.client.post<FindexUser>('/user/opportunitiesOptIn');
  }

  async getOpportunityById(opportunityId: string) {
    return await this.client.get<ISavedDocument<IOpportunity>>(`/opportunity/${opportunityId}`);
  }

  async getOpportunityInvestment(opportunityId: string) {
    return await this.client.get<FindexInvestment>(`/opportunity/${opportunityId}/investment`);
  }

  async getOpportunityInquires(opportunityId: string, options: {
    offset?: number;
    limit?: number;
  }) {
    const params = urlEncode({...options, count: true, offset: 0, limit: 0});
    return await this.client.get<ISavedDocument<IOpportunityInquire> []>(`/opportunity/${opportunityId}/inquire?${params}`)
  }

  async agreeAIAssistantTerms() {
    return await this.client.post<any>('/assistant/agreeTermsOfUse', { payload: {} });
  }

  async getPersonalReferralCode() {
    return await this.client.get<IKeyCode>(`/user/referralCode`);
  }

  async getFlanksConnectors() {
    return await this.client.get<Array<{  name: string; id: string }>>(`/flanks/connectors`);
  }

  async createFlanksSession(connectorId: string, options: {
    context?: Record<string, any>,
    labels?: Record<string, string>,
    redirectUrl?: string
  } = {}) {
    return await this.client.post<FlanksCreateSession>(`/flanks/createSession`, {
      payload: {
        connectorId,
        ...(options.context ? { context: options.context } : {}),
        ...(options.labels ? { labels: options.labels } : {}),
        ...(options.redirectUrl ? { redirectUrl: options.redirectUrl } : {})
      }
    })
  }


  async getFlanksSession(sessionId: string) {
    return await this.client.get<FlanksSession>(`/flanks/session/${sessionId}`);
  }

  async getFlanksInvestments(connectionId: string) {
    return await this.client.post<Array<FlanksInvestment>>(`/flanks/investments`, {
      payload: {
        connectionId
      }
    })
  }

  async getPotentialFlanksInvestments(connectionId: string) {
    return await this.client.post<Array<PotentialInvestment>>(`/flanks/investments/potential`, {
      payload: {
        connectionId
      }
    })
  }

  async checkDirectoryHasDocuments(assetIds: string[]) {
    return await this.client.post<Array<{ assetId: string; hasDocuments: boolean }>>(`/asset/directory/hasDocuments`, {
      payload: { assetIds }
    })
  }

  async deleteAssetDirectory(assetId: string) {
    return await this.client.delete(`/asset/${assetId}/directory`)
  }
}

export const FindexClient = new FindexAPIClient();
